import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Divider } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ServerCardStatus from '../../../PublicSide/components/ServerCardStatus/ServerCardStatus';
import Input from '../../../PublicSide/components/Input/Input';
import Button from '../../../PublicSide/components/Button/Button';
import { styled } from '@mui/system';
import AirlineStopsOutlinedIcon from '@mui/icons-material/AirlineStopsOutlined';
import { ApiGetApplications, ApplicationDeployed, ApplicationUpdated } from '../../../stores/api/application/actions';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { StyledBox } from '../../../style/GlobalsComponentsStyled';
import ServiceLocator from '../../../services/ServiceLocator';
import { WithRefreshToken } from '../../../stores/auth/actions';
import { useAppDispatch } from '../../../helpers/UseAppDispatch';
import { HandleError, HandleSuccess } from '../../../stores/page/actions';
import { GameResponse } from '../../../api/dtos/application/GameResponse';
import { Percentage } from '../../../api/dtos/application/Percentage';
import SuccessErrorMessage from '../SuccesErrorMessage/SuccessErrorMessage';

interface ApplicationCardProps {
    id: number
    game?: GameResponse,
    name: string,
    description: string,
    httpLogin?: string,
    version: string,
    percentage: Percentage,
    lastUpdate: string
}

const StyledIconButton = styled(IconButton)({
    backgroundColor: 'black',
    color: 'white',
    borderRadius: 8,
    width: 36,
    height: 36,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
        backgroundColor: '#1C1D2B',
    }
});

const StyleButtonBox = styled(Box)({
    display: 'flex',
    gap: '1rem',
    width: '100%',
    marginTop: '1rem'
})

const StyledBoxContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    height: '100%'
})
const ApplicationCard: React.FC<ApplicationCardProps> = ({ id, game, name, description, httpLogin, version, percentage, lastUpdate }) => {
    const dispatch = useAppDispatch();
    const [editMode, setEditMode] = useState<'none' | 'nameDesc' | 'version'>('none');
    const [updatedName, setUpdatedName] = useState(name);
    const [updatedDescription, setUpdatedDescription] = useState(description);
    const [updatedVersion, setUpdatedVersion] = useState(version);
    const [errorMessage, setErrorMessage] = useState("");
    const Navigate = useNavigate();

    const handleUpdateApplication = async () => {
        if (updatedName === name && updatedDescription === description) {
            setErrorMessage("Please fill one of the fields.");
            return;
        }
        const request = {
            name: updatedName,
            description: updatedDescription,
        };
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ApplicationService.UpdateApplication(id, request);
            if (response.is_success) {
                dispatch(ApplicationUpdated(id, response));
                dispatch(ApiGetApplications());
                dispatch(HandleSuccess('Application updated successfully!'));
                setEditMode('none');
            }
            else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };

    const handleDeployApplication = async () => {
        if (updatedVersion === version) {
            setErrorMessage("Please fill the field");
            return;
        }
        await WithRefreshToken(async () => {
            const response = await ServiceLocator.ApplicationService.DeployApplication(id, updatedVersion);
            if (response.is_success) {
                dispatch(ApplicationDeployed());
                dispatch(ApiGetApplications());
                dispatch(HandleSuccess('Application deployed successfully!'));
                setEditMode('none');
            }
            else {
                dispatch(HandleError(response));
            }
        }, dispatch);
    };


    return (
        <Card sx={{ borderRadius: 2, boxShadow: 3, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', color: 'black' }}>
            <SuccessErrorMessage errorMessage={errorMessage} onClear={() => { setErrorMessage(''); }} />
            <CardContent sx={{ padding: '1rem', height: '100%' }}>
                {editMode === 'nameDesc' ? (
                    <StyledBoxContainer>
                        <StyledBox>
                            <Input type='text' label='Name' name='name' placeholder={name} onChange={(e) => setUpdatedName(e.target.value)} required />
                            <Input type='text' label='Description' name='description' placeholder={description} onChange={(e) => setUpdatedDescription(e.target.value)} required />
                        </StyledBox>
                        <StyleButtonBox>
                            <Button text='Cancel' onClick={() => setEditMode('none')} />
                            <Button type='submit' text='Submit' onClick={handleUpdateApplication} />
                        </StyleButtonBox>
                    </StyledBoxContainer>
                ) : editMode === 'version' ? (
                    <StyledBoxContainer>
                        <Box>
                            <Input type='text' label='Version number' name='version' placeholder={version} onChange={(e) => setUpdatedVersion(e.target.value)} required />
                        </Box>
                        <StyleButtonBox>
                            <Button text='Cancel' onClick={() => setEditMode('none')} />
                            <Button type='submit' text='Submit' onClick={handleDeployApplication} />
                        </StyleButtonBox>
                    </StyledBoxContainer>
                ) : (
                    <StyledBoxContainer onClick={() => Navigate(`/application/${id}/servers`)}>
                        <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
                            <StyledBox>
                                <Typography variant='h3'>{name}</Typography>
                                <Typography>{description}</Typography>
                                <Typography color='text.secondary'>Version: {version}</Typography>
                            </StyledBox>
                            <Box display='flex' gap={1}>
                                <StyledIconButton onClick={(e) => { e.stopPropagation(); setEditMode('nameDesc') }} aria-label='edit name and/or description'>
                                    <EditOutlinedIcon />
                                </StyledIconButton>
                                <StyledIconButton onClick={(e) => { e.stopPropagation(); setEditMode('version') }} aria-label='deploy newversion'>
                                    <AirlineStopsOutlinedIcon />
                                </StyledIconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <StyledBox>
                            <ServerCardStatus percentage={percentage} detailed />
                            <Typography>
                                Last update: {format(new Date(lastUpdate), "MMMM d, yyyy 'at' h:mm a")}
                            </Typography>
                        </StyledBox>
                    </StyledBoxContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default ApplicationCard;